/**
 * i18n module — createI18n() factory.
 *
 * Returns:
 *  - t(key, params?)  — translation lookup with interpolation
 *  - dateLocale        — computed BCP 47 locale string for formatDate()
 *  - locale            — current detected locale (reactive ref)
 *  - loadPacks(packs)  — load customer language packs (called once in wr_init)
 */
import { ref, computed, watch, type ComputedRef, type Ref } from 'vue';
import { defaultStrings } from './defaults';
import { detectLanguage, detectRegionLocale } from './detection';

import type { LanguageDictionary } from '../types/crowdhandler';

const DEFAULT_CDN_BASE = 'https://crowdhandler-i18n.s3.us-east-1.amazonaws.com/';

/**
 * Marker attribute on <html> that opts a template into CrowdHandler-managed
 * localization. When present, the i18n module owns the document's lang/dir
 * (the multi-language template carries it). Single source of truth — the
 * shipped multi-language templates must use this exact attribute name.
 */
export const CH_I18N_ATTR = 'data-ch-i18n';

/**
 * Fetch a single language pack from the CDN.
 * Returns null silently on any error (network, 404, parse).
 */
async function fetchLanguagePack(
    lang: string,
    baseUrl: string = DEFAULT_CDN_BASE,
): Promise<Record<string, string> | null> {
    try {
        const url = `${baseUrl.replace(/\/$/, '')}/${lang}.json`;
        const res = await fetch(url);
        if (!res.ok) return null;
        return await res.json();
    } catch {
        return null;
    }
}

/** 2-char code → BCP 47 mapping for dateLocale */
const localeToBCP47: Record<string, string> = {
    en: 'en-GB',
    de: 'de-DE',
    fr: 'fr-FR',
    es: 'es-ES',
    it: 'it-IT',
    nl: 'nl-NL',
    pt: 'pt-PT',
    pl: 'pl-PL',
    sv: 'sv-SE',
    da: 'da-DK',
    no: 'nb-NO',
    fi: 'fi-FI',
    cs: 'cs-CZ',
    ja: 'ja-JP',
    ko: 'ko-KR',
    zh: 'zh-CN',
    ru: 'ru-RU',
    ar: 'ar-SA',
    tr: 'tr-TR',
    hu: 'hu-HU',
    ro: 'ro-RO',
    el: 'el-GR',
    he: 'he-IL',
    th: 'th-TH',
    vi: 'vi-VN',
    uk: 'uk-UA',
    hr: 'hr-HR',
    sk: 'sk-SK',
    bg: 'bg-BG',
    sr: 'sr-RS',
    lt: 'lt-LT',
    lv: 'lv-LV',
    et: 'et-EE',
    sl: 'sl-SI',
    ca: 'ca-ES',
    eu: 'eu-ES',
    gl: 'gl-ES',
    cy: 'cy-GB',
    ga: 'ga-IE',
    hi: 'hi-IN',
    ta: 'ta-IN',
    bn: 'bn-IN',
    te: 'te-IN',
    mr: 'mr-IN',
    ur: 'ur-PK',
    gu: 'gu-IN',
    pa: 'pa-IN',
    kn: 'kn-IN',
    ml: 'ml-IN',
    fa: 'fa-IR',
    tl: 'tl-PH',
    sw: 'sw-KE',
};

/** Languages that use right-to-left script */
const rtlLanguages = new Set(['ar', 'he', 'fa', 'ur']);

/**
 * Resolve the BCP 47 locale used for date/number formatting.
 *
 * Priority:
 *   1. The content locale itself if it already carries a region (e.g. 'en-US').
 *   2. The visitor's browser region when it's the *same language* as the content
 *      — so an American on an English room gets 'en-US' (12h, M/D/Y) and a Brit
 *      gets 'en-GB' (24h, D/M/Y), even though both load the 2-char 'en' pack.
 *   3. The language's canonical region from the map (e.g. content 'fr' on a
 *      non-French browser -> 'fr-FR', keeping the page's formatting consistent
 *      with its language).
 *   4. 'default' (the browser default) when nothing maps.
 *
 * @param contentLocale  The active translation locale (e.g. 'en', 'fr', 'pt-BR').
 * @param browserLocale  The visitor's normalized browser locale (e.g. 'en-US').
 */
export function resolveDateLocale(contentLocale: string, browserLocale: string): string {
    if (contentLocale.includes('-')) return contentLocale;
    if (browserLocale.includes('-') && browserLocale.split('-')[0] === contentLocale) {
        return browserLocale;
    }
    return localeToBCP47[contentLocale] || 'default';
}

export type InterpolationParams = Record<string, string | number | null | undefined>;

/**
 * Replace {name} placeholders in a translation string with param values.
 *
 * Null/undefined-valued params render as an empty string — never the literal
 * text "null"/"undefined". This matters because templates pass reactive values
 * straight through (e.g. `t('estimate-eta', { time: ch.eta })`), and ch.eta is
 * null when the ETA is hidden. A param key that is entirely absent keeps its
 * `{name}` placeholder, which signals a genuinely missing interpolation.
 */
export function interpolate(raw: string, params?: InterpolationParams): string {
    if (!params) return raw;
    return raw.replace(/\{(\w+)\}/g, (_, name) => {
        if (!(name in params)) return `{${name}}`;        // unknown placeholder kept
        const value = params[name];
        if (value === null || value === undefined) return ''; // never render "null"/"undefined"
        return String(value);
    });
}

export interface I18nInstance {
    /** True when the template carries CH_I18N_ATTR and opted into CrowdHandler-managed localization. */
    enabled: boolean;
    t: (key: string, params?: InterpolationParams) => string;
    dateLocale: ComputedRef<string>;
    isRTL: ComputedRef<boolean>;
    locale: Ref<string>;
    loadPacks: (packs: LanguageDictionary) => void;
    fetchCdnPack: (lang: string, baseUrl?: string) => Promise<void>;
}

/**
 * Create an i18n instance.
 * Call once per composable lifecycle.
 */
export function createI18n(): I18nInstance {
    let customerPacks: LanguageDictionary = {};

    // Single opt-in for CrowdHandler-managed localization: the template carries
    // CH_I18N_ATTR on <html> (the shipped multi-language template does). Without
    // it the template is treated as fixed-language: we never write <html> lang/dir
    // and we never request a pack from the i18n CDN. That second part matters for
    // customers whose network policy blocks cross-region requests — a standard or
    // custom template must not reach out to the CDN at all. Customer packs passed
    // via window.CH_languages still load; they involve no network request.
    // Read once from the static attribute so the decision is stable across
    // async pack loads.
    const enabled =
        typeof document !== 'undefined' &&
        document.documentElement.hasAttribute(CH_I18N_ATTR);
    // Detect language from environment signals (URL param, html lang, navigator, referrer)
    // No packs loaded yet — returns best-guess so we know which CDN pack to fetch
    const locale = ref(detectLanguage());

    /**
     * Load customer language packs and re-detect locale.
     * Called in wr_init() after reading window.CH_languages.
     */
    function loadPacks(packs: LanguageDictionary): void {
        // Merge customer packs over built-in — customer strings win per-key
        for (const lang of Object.keys(packs)) {
            customerPacks[lang] = { ...customerPacks[lang], ...packs[lang] };
        }
        const allAvailable = new Set(Object.keys(customerPacks));
        locale.value = detectLanguage(allAvailable);
    }

    /**
     * Translate a key with optional interpolation.
     *
     * Lookup chain (regional locales fall through to base language):
     * 1. customerPacks[locale][key]           — e.g. 'pt-BR'
     * 2. customerPacks[baseLang][key]          — e.g. 'pt' (only if locale had a region)
     * 3. customerPacks['en'][key]
     * 4. defaultStrings[key]
     *
     * Interpolation: {name} placeholders replaced by params values.
     */
    function t(key: string, params?: InterpolationParams): string {
        const lang = locale.value;
        const base = lang.split('-')[0];
        const hasRegion = base !== lang;

        const raw =
            customerPacks[lang]?.[key] ??
            (hasRegion ? customerPacks[base]?.[key] : undefined) ??
            customerPacks['en']?.[key] ??
            defaultStrings[key] ??
            key; // fall through to key name if nothing found

        return interpolate(raw, params);
    }

    /**
     * Fetch a language pack from the CDN and merge it into the store.
     *
     * For regional locales (e.g., 'pt-BR'), tries the full code first. If the
     * regional pack isn't on the CDN, falls back to the base language ('pt').
     * Customer strings (from CH_languages / loadPacks) always win over CDN strings.
     * No-op for 'en' (English defaults are bundled) and no-op entirely when the
     * template lacks CH_I18N_ATTR (see `enabled`).
     */
    async function fetchCdnPack(lang: string, baseUrl?: string): Promise<void> {
        if (!enabled) return; // template has not opted into i18n — no CDN request
        if (lang === 'en' || lang.startsWith('en-')) return;

        // Try the full locale first (e.g., 'pt-BR')
        let pack = await fetchLanguagePack(lang, baseUrl);
        let storedAs = lang;

        // Fall back to base language if regional pack isn't available
        if (!pack && lang.includes('-')) {
            const base = lang.split('-')[0];
            if (base !== 'en' && !customerPacks[base]) {
                pack = await fetchLanguagePack(base, baseUrl);
                storedAs = base;
            }
        }

        if (pack) {
            // CDN goes under customer — existing customer keys win
            customerPacks[storedAs] = { ...pack, ...customerPacks[storedAs] };
            // Re-detect with updated availability
            const allAvailable = new Set([...Object.keys(customerPacks), 'en']);
            locale.value = detectLanguage(allAvailable);
        }
    }

    /**
     * BCP 47 locale for use with formatDate(). See resolveDateLocale for the
     * precedence (content region -> visitor's region for the same language ->
     * language's canonical region -> browser default). `browserLocale` is
     * captured once: the visitor's region (e.g. 'en-US') so regional date
     * formatting survives even when the content language is a 2-char pack.
     */
    const browserLocale = detectRegionLocale();
    const dateLocale = computed<string>(() => resolveDateLocale(locale.value, browserLocale));

    /**
     * Whether the current locale uses right-to-left script.
     */
    const isRTL = computed<boolean>(() => rtlLanguages.has(locale.value));

    // Own <html> lang/dir only when this template opts in (see `enabled`).
    // Standard and custom templates that manage language themselves are left
    // untouched, so we never flip dir on fixed-language content or fight a
    // custom switcher. The immediate fire applies dir at first paint even when
    // the detected locale doesn't change across a later pack load.
    watch(locale, (lang) => {
        if (!enabled) return;
        document.documentElement.lang = lang;
        document.documentElement.dir = rtlLanguages.has(lang) ? 'rtl' : 'ltr';
    }, { immediate: true });

    return {
        enabled,
        t,
        dateLocale,
        isRTL,
        locale,
        loadPacks,
        fetchCdnPack,
    };
}
